import {debounce} from "../utils/debounce";

interface TermCompare {
    id: number
    type?: string
}

interface SearchField {
    type: string
    apiUrl: string
    isLoading: boolean
    noResults: boolean
    searchQuery: string
    searchResults: SearchResult[]
    selectedTerms: SearchResult[]
    performSearch: (...args: never[]) => void
    clearResults(): void
    compareTerm(term: TermCompare, result: SearchResult): void
    selectTerm(term: TermCompare): void
    readonly matchingTermFromQuery: SearchResult | undefined
    readonly canCreateCustomTerm: boolean
    addCustomQuery(): void
    removeTerm(term: TermCompare): void
    popTermWithDelete(e: KeyboardEvent): void
    formatLabel(label: string): string
    selectMatchingTerm(event: FocusEvent): void
    selectMostSimilarTerm(): void
    setLocalityRadius(id: number, radius: number): void
    handleDropdownOutsideClick(e: Event, dropdown: HTMLDetailsElement): void
    setUpDropdownListeners(e: Event): void
}

export default function (props: { type: "interest" | "locality", selected: SearchResult[] }): SearchField {
    async function getSearchOptions() {
        if (this.searchQuery === '') {
            this.clearResults()
            return
        }

        if (this.searchQuery.length < 2) return

        this.isLoading = true
        await fetch(`${this.apiUrl}/?query=${this.searchQuery}`)
            .then((response) => response.json())
            .then((data: SearchResult[]) => {
                const selectedIDs = this.selectedTerms.map((term: SearchResult) => term.id)
                this.searchResults = data.filter(result => !selectedIDs.includes(result.id))
                this.noResults = !this.searchResults.length
            })
            .finally(() => {
                this.isLoading = false
            })
    }

    return {
        type: props.type,
        apiUrl: `/wp-json/search/${props.type}`,
        isLoading: false,
        noResults: false,
        searchQuery: '',
        searchResults: [],
        selectedTerms: props.selected,
        performSearch: debounce(getSearchOptions, 240),
        clearResults() {
            this.searchQuery = ''
            this.searchResults = []
            this.noResults = false
        },
        compareTerm(term, result) {
            return Object.keys(term).every(key => term[key] === result[key])
        },
        selectTerm(term) {
            props.selected.push(this.searchResults.find((result: SearchResult) => this.compareTerm(term, result)))
            this.clearResults()
        },
        get matchingTermFromQuery() {
            return this.searchResults
                .find((result: SearchResult) => this.searchQuery.toLowerCase() === result.name.toLowerCase())
        },
        get canCreateCustomTerm() {
            return this.type === "interest" && !this.matchingTermFromQuery && !this.selectedTerms
                .find((result: SearchResult) => this.searchQuery.toLowerCase() === result.name.toLowerCase())
        },
        addCustomQuery() {
            this.searchQuery && props.selected.push({
                id: parseInt([...this.searchQuery.toLowerCase()]
                    .reduce((id, currentChar) => id + currentChar.charCodeAt(0), '')),
                name: this.searchQuery,
                slug: this.searchQuery,
                type: 'search'
            }) && this.clearResults()
        },
        removeTerm(term) {
            props.selected.splice(props.selected
                .findIndex((result: SearchResult) => this.compareTerm(term, result)), 1)
            this.selectedTerms = props.selected
        },
        popTermWithDelete(e) {
            if (this.searchQuery.length || !["Delete", "Backspace"].includes(e.key)) return

            props.selected.pop()
            this.selectedTerms = props.selected
        },
        formatLabel(label) {
            const regexp = new RegExp(`${this.searchQuery}`, 'i')
            return label?.replace(regexp, '<b>$&</b>') ?? ''
        },
        selectMatchingTerm(event) {
            if (this.$refs.fieldset.contains(event.relatedTarget as Node) || !this.searchQuery.length) return

            const matchingTerm = this.matchingTermFromQuery
            matchingTerm ? this.selectTerm(matchingTerm) : this.canCreateCustomTerm
                ? this.addCustomQuery() : this.clearResults()
        },
        selectMostSimilarTerm() {
            this.searchQuery.length && this.canCreateCustomTerm ? this.addCustomQuery()
                : this.searchResults.length && this.selectTerm(this.searchResults[0])
        },
        setLocalityRadius(id, radius) {
            props.selected.forEach(result => {
                if (result.type !== "city" || id !== result.id) return
                result.radius = radius
            })

            this.selectedTerms = props.selected

            const dropdown = document.querySelector<HTMLDetailsElement>(`[data-field-dropdown="${id}"]`)
            if (dropdown) dropdown.open = false
        },
        handleDropdownOutsideClick(e, dropdown) {
            if (dropdown.contains(e.target as HTMLElement)) return
            dropdown.open = false
        },
        setUpDropdownListeners(e) {
            const detail = e.target as HTMLDetailsElement
            document[detail.open ? "addEventListener" : "removeEventListener"]
                ('click', e => this.handleDropdownOutsideClick(e, detail))
        }
    }
}
